import React, { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Places API caps a location bias circle at 50 km.
const BIAS_RADIUS_M = 50000;

const component = (components, type) =>
  components.find((c) => c.types.includes(type))?.longText || "";

/** Splits Google's address components into the publish form's fields. */
export const toAddressFields = (components, fallbackAddress) => {
  const street = [component(components, "route"), component(components, "street_number")]
    .filter(Boolean)
    .join(" ");
  return {
    address: street || fallbackAddress,
    city:
      component(components, "locality") ||
      component(components, "sublocality") ||
      component(components, "administrative_area_level_2"),
    province: component(components, "administrative_area_level_1"),
    country: component(components, "country"),
  };
};

const SuggestField = ({
  value,
  onChange,
  onSelect,
  label,
  disabled,
  types,
  regionCode,
  bias,
  context,
  fields,
}) => {
  const places = useMapsLibrary("places");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // One session token spans the suggestions and the final details lookup, which Google bills as one session.
  const sessionToken = useRef(null);
  // Picking a suggestion rewrites the text; don't search again for that text.
  const selectedText = useRef(null);

  const biasLat = bias?.lat;
  const biasLng = bias?.lng;
  const typesKey = types?.join(",");

  useEffect(() => {
    if (!places) return undefined;
    const query = value.trim();
    if (query.length < 2 || value === selectedText.current) {
      setOptions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!sessionToken.current) sessionToken.current = new places.AutocompleteSessionToken();
      setLoading(true);
      try {
        // e.g. "San Martín 100, Villa Carlos Paz": far better than bias alone at keeping results in one town.
        const input = context ? `${query}, ${context}` : query;
        const request = { input, sessionToken: sessionToken.current, language: "es" };
        if (typesKey) request.includedPrimaryTypes = typesKey.split(",");
        if (regionCode) request.includedRegionCodes = [regionCode.toLowerCase()];
        if (Number.isFinite(biasLat) && Number.isFinite(biasLng)) {
          request.locationBias = { center: { lat: biasLat, lng: biasLng }, radius: BIAS_RADIUS_M };
        }
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (cancelled) return;
        setOptions(
          suggestions
            .map((s) => s.placePrediction)
            .filter(Boolean)
            .map((p) => ({
              id: p.placeId,
              text: p.text.text,
              main: p.mainText?.text || p.text.text,
              secondary: p.secondaryText?.text || "",
              prediction: p,
            }))
        );
      } catch (error) {
        console.error("Error buscando sugerencias:", error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, places, typesKey, regionCode, biasLat, biasLng, context]);

  const handleSelect = async (option) => {
    if (!option || typeof option === "string") return;
    selectedText.current = option.main;
    onChange(option.main);
    setOptions([]);
    try {
      const place = option.prediction.toPlace();
      await place.fetchFields({ fields });
      const text = onSelect(place, option) ?? option.main;
      selectedText.current = text;
    } catch (error) {
      console.error("Error obteniendo el lugar:", error);
    } finally {
      sessionToken.current = null;
    }
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      disabled={disabled}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.text)}
      inputValue={value}
      onInputChange={(_, text, reason) => {
        if (reason === "input" || reason === "clear") onChange(text);
      }}
      onChange={(_, option) => handleSelect(option)}
      loading={loading}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <PlaceIcon sx={{ mr: 1.5, color: "primary.main", fontSize: 20 }} />
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {option.main}
            </Typography>
            {option.secondary && (
              <Typography variant="caption" color="text.secondary">
                {option.secondary}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          margin="dense"
          label={label}
          // Keep the browser's own address autofill from covering Google's suggestions.
          inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/**
 * Text field with Google Places suggestions (falls back to a plain field without an API key).
 * - `types`: Places primary types to suggest, e.g. ["(cities)"]; all kinds when omitted.
 * - `regionCode`: ISO country code the suggestions are limited to.
 * - `bias`: { lat, lng } to prefer nearby results.
 * - `context`: text appended to the query (e.g. the chosen locality) to narrow results.
 * - `fields`: Place fields fetched on selection, passed to `onSelect(place, option)`;
 *   `onSelect` may return the text to leave in the field.
 */
const GooglePlaceAutocomplete = ({ fields = ["addressComponents"], ...props }) => {
  if (!API_KEY) {
    return (
      <TextField
        margin="dense"
        fullWidth
        label={props.label}
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
      />
    );
  }
  return (
    <APIProvider apiKey={API_KEY} language="es">
      <SuggestField fields={fields} {...props} />
    </APIProvider>
  );
};

export default GooglePlaceAutocomplete;
